import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/useToast";
import { useNavigate } from "react-router-dom";
import { UserPlus, Plus, Building2 } from "lucide-react";
import { createEmployee } from "../api/employees";
import { useAuth } from "../contexts/AuthContext";
import { createOffice, getOffice, getMyPublicIp, uploadLogo, uploadSignature } from "../api/officeandcompany";
import { getBrowserCoordinates } from "../api/attendance";


/** A typed coordinate as a plain signed decimal, or null when it isn't one.
 *
 * hrms parses office coordinates with a bare `float()` (normalize_float), which
 * RAISES on anything but a plain decimal — and a row whose latitude won't parse
 * is dropped from the geofence candidates entirely, silently. A real office was
 * stored as "28.7114861° N" and was therefore invisible: every employee
 * standing in it was measured against the next office 6.5 km away and refused.
 * Degree signs and hemisphere letters are what people paste out of Google Maps,
 * so accept them here and normalise, rather than storing them and failing later.
 */
const parseCoordinate = (raw: unknown): number | null => {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (!text) return null;
  // The hemisphere letter carries the sign: "28.71 S" is -28.71. Read it before
  // stripping, or a southern/western office silently lands in the wrong
  // hemisphere — a failure that looks like "you're 3000 km away".
  const hemisphere = text.match(/([NSEW])\s*$/i)?.[1]?.toUpperCase();
  const numeric = Number(text.replace(/°/g, "").replace(/[NSEW]/gi, "").trim());
  if (!Number.isFinite(numeric)) return null;
  return hemisphere === "S" || hemisphere === "W" ? -Math.abs(numeric) : numeric;
};


const AddOffice = () => {
  const [loading, setLoading] = useState(false);
  const [offices, setOffices] = useState<any[]>([]);
  const emptyOffice: any = {
    id: undefined,
    office_name: "",
    location: "",
    working_hours_start: "",
    working_hours_end: "",
    wifi_ssid: "",
    allowed_ips: "",
    lattitude: null,
    longitude: null,
    radius_meters: null,
    is_active: true,
  };
  const [employeeData, setEmployeeData] = useState({
    office_name: "",
    location: "",
    working_hours_start: "",
    working_hours_end: "",
    wifi_ssid: "",
    allowed_ips: "",
    lattitude: null,
    longitude: null,
    radius_meters: null,
    is_active: true,
  });
  // The address THIS browser reaches the backend from, read once on open so it
  // can be offered as a suggestion instead of hidden behind a button the admin
  // has to press on faith. Note it is the same value for every device on the
  // office network — Ethernet, Wi-Fi or a phone all leave through the same
  // router — so the admin only has to be in the office, not on a cable.
  const [detectedIp, setDetectedIp] = useState<string | null>(null);
  const [detectingIp, setDetectingIp] = useState(true);
  const [locating, setLocating] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  /** Fill the geofence from the device standing in the office.
   *
   * Typed coordinates are how an office ends up 1.5 km from where people
   * actually sit: a pin dropped on the wrong building, a truncated decimal, or
   * latitude and longitude swapped all look perfectly plausible in a text box
   * and only surface weeks later as "you're 1691m from VR". Reuses the exact
   * accuracy race the clock-in uses, so the coordinates saved here are drawn
   * from the same source the geofence will later measure against.
   */
  const useCurrentLocation = async () => {
    setLocating(true);
    try {
      const fix = await getBrowserCoordinates();
      setEmployeeData((prev: any) => ({
        ...prev,
        lattitude: String(fix.latitude),
        longitude: String(fix.longitude),
        // Don't overwrite a radius the admin already chose.
        radius_meters: prev.radius_meters === "" || prev.radius_meters === null || prev.radius_meters === undefined
          ? 150
          : prev.radius_meters,
      }));
      toast({
        description: fix.accuracy != null
          ? `Location captured (accurate to about ${Math.round(fix.accuracy)}m). Check the radius covers the building.`
          : "Location captured. Check the radius covers the building.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        description: error?.message || "Couldn't read this device's location.",
      });
    } finally {
      setLocating(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    getMyPublicIp()
      .then((ip) => {
        if (!cancelled) setDetectedIp(ip);
      })
      .finally(() => {
        if (!cancelled) setDetectingIp(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const allowedIpList = String((employeeData as any).allowed_ips || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
  // "203.0.113.7" and "203.0.113.7/32" are the same single address written two
  // ways, and an admin may well have typed the second. Without this the
  // suggestion below would offer to add an IP that is already on the list.
  const asSingleAddress = (entry: string) => entry.replace(/\/(32|128)$/, "");
  const detectedIpAlreadyAdded =
    !!detectedIp && allowedIpList.some((entry) => asSingleAddress(entry) === detectedIp);

  const addDetectedIp = () => {
    if (!detectedIp || detectedIpAlreadyAdded) return;
    handleInputChange("allowed_ips", [...allowedIpList, detectedIp].join(", "));
    toast({ description: `${detectedIp} added — remember to save.` });
  };
  const isReadOnly = ["employee", "intern", "supervisor"].includes(user?.role || "");
  const navigate = useNavigate();


  // `selectId` is the office to stay on once the rows come back: the one just
  // saved. Without it a save landed the admin on offices[0] — edit the second
  // office, hit Save, and the form switched to the first one.
  const fetchEmployees = async (selectId?: string) => {
    try {
      setLoading(true);
      console.log("Fetching employees...");
      const response = await getOffice();
    //   console.log(
    //     "Employees fetched successfully:",
    //     response.office?.length || 0
    //   );
      console.log(response.offices,'response.office[0]')
      const rows = response?.offices || [];
      setOffices(rows);
      setEmployeeData((prev) => {
        // Keep the admin on the office they were on; first office on open.
        const keepId = selectId ?? (prev as any)?.id;
        return rows.find((office: any) => office.id === keepId) || rows[0] || emptyOffice;
      });
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast({
        // title: "Error",
        description: error.message || "Failed to load employees",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);


  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (isReadOnly) return;

  try {

    setLoading(true);

    let logoUrl = "";
    let signatureUrl = "";

    // ✅ call TS API function
    if (employeeData.upload_logo) {
      const logoRes = await uploadLogo(employeeData.upload_logo);
      // New shape: { data: { logoPath, logoUrl } } | legacy: { url } / { path }
      logoUrl = logoRes?.data?.logoUrl ?? logoRes?.url ?? logoRes?.path ?? "";
    }

    // ✅ call TS API function
    if (employeeData.upload_signature) {
      const signRes = await uploadSignature(employeeData.upload_signature);
      signatureUrl = signRes?.data?.signatureUrl ?? signRes?.url ?? signRes?.path ?? "";
    }

    // The radius input is a text <Input>, so clearing it leaves "" in
    // state. Backend `radius_meters` is Optional[float] and rejects "".
    // Normalize empty / non-numeric values to null before sending.
    const rawRadius = (employeeData as any).radius_meters;
    const normalizedRadius =
      rawRadius === "" || rawRadius === null || rawRadius === undefined
        ? null
        : Number.isFinite(Number(rawRadius))
          ? Number(rawRadius)
          : null;

    // ------------------------------------------------------------------
    // GEOFENCE VALIDATION — an office saved half-configured is a DEAD office
    // ------------------------------------------------------------------
    // hrms only treats an office as a geofence candidate when latitude,
    // longitude AND radius_meters > 0 all parse. A row missing any of them is
    // skipped in silence: nobody standing in that office can ever clock in,
    // and the refusal they get names some other office kilometres away. The
    // form used to save exactly that, so these three checks are the whole
    // reason three live offices went dark.
    const rawLat = String((employeeData as any).lattitude ?? "").trim();
    const rawLon = String((employeeData as any).longitude ?? "").trim();
    const lat = parseCoordinate(rawLat);
    const lon = parseCoordinate(rawLon);

    const reject = (description: string) => {
      toast({ variant: "destructive", description });
      setLoading(false);
    };

    if ((rawLat && lat === null) || (rawLon && lon === null)) {
      return reject("Latitude and longitude must be plain decimals, e.g. 28.674257 and 77.129646.");
    }
    if (Boolean(rawLat) !== Boolean(rawLon)) {
      return reject("Enter both latitude and longitude, or leave both blank.");
    }
    if (lat !== null && (lat < -90 || lat > 90)) {
      return reject("Latitude must be between -90 and 90. Check you haven't swapped it with longitude.");
    }
    if (lon !== null && (lon < -180 || lon > 180)) {
      return reject("Longitude must be between -180 and 180. Check you haven't swapped it with latitude.");
    }
    const hasCoordinates = lat !== null && lon !== null;
    if (hasCoordinates && !(normalizedRadius !== null && normalizedRadius > 0)) {
      return reject("An office with coordinates needs a radius in metres (e.g. 150), or nobody can clock in there.");
    }
    if (!hasCoordinates && normalizedRadius !== null && normalizedRadius > 0) {
      return reject("A radius needs latitude and longitude to apply to.");
    }

    // ✅ create office payload — coordinates stored in the plain decimal form
    // hrms can actually parse, not whatever shape was pasted in.
    const payload = {
      ...employeeData,
      lattitude: hasCoordinates ? String(lat) : null,
      longitude: hasCoordinates ? String(lon) : null,
      radius_meters: normalizedRadius,
      upload_logo: logoUrl,
      upload_signature: signatureUrl,
    };

    const response = await createOffice(payload);

    if (response.success) {

      // Stay on what was just saved — the id of the updated office, or of the
      // one the backend just created when this was an "Add another office".
      await fetchEmployees(response?.employee?.office?.id ?? (employeeData as any).id);

      toast({
        title: "Success",
        description: (employeeData as any).id ? "Office updated successfully" : "Office added successfully",
      });

    }

  } catch (error: any) {

    toast({
      variant: "destructive",
      // title: "Error",
      description: error.message,
    });

  } finally {

    setLoading(false);

  }

};

  const handleInputChange = (field: string, value: string) => {
    console.log(`AddEmployee: ${field} field changed to:`, value);
    setEmployeeData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };
      console.log(`AddEmployee: Updated employee data:`, newData);
      return newData;
    });
  };

  if (loading) {
  return (
    <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
  );
}

  return (
    <div className="space-y-6 mx-4 my-8">
      {!isReadOnly && (
        <div className="flex flex-wrap items-center gap-2">
          {offices.map((office) => (
            <Button
              key={office.id}
              type="button"
              variant={(employeeData as any).id === office.id ? "default" : "outline"}
              onClick={() => setEmployeeData(office)}
            >
              <Building2 className="mr-2 h-4 w-4" />
              {office.office_name || "Office"}
            </Button>
          ))}
          <Button type="button" variant="outline" onClick={() => setEmployeeData(emptyOffice)}>
            <Plus className="mr-2 h-4 w-4" />
            Add another office
          </Button>
        </div>
      )}
      {/* <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            console.log("AddEmployee: Back button clicked");
            navigate('/employees');
          }}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Employees
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Add New Employee</h1>
          <p className="text-muted-foreground">
            Create a new employee record in the system.
          </p>
        </div>
      </div> */}

      <Card className="max-w-1xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Office Information
          </CardTitle>
          <CardDescription>
            Enter the details for the new office. Fields marked with{" "}
            <span className="font-semibold">*</span> are required; the rest are
            optional but recommended for in-office attendance checks.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="name">Office Names *</Label>
                <Input
                  id="name"
                  value={employeeData.office_name}
                  onChange={(e) => {
                    handleInputChange("office_name", e.target.value);
                  }}
                  placeholder="Enter office name"
                  required
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="working_hours_start">
                  Working Hours Start *
                </Label>
                <Input
                  id="working_hours_start"
                  type="time"
                  value={(employeeData.working_hours_start || "").slice(0, 5)}
                  onChange={(e) => {
                    const v = e.target.value;
                    const prev = (
                      employeeData.working_hours_start || ""
                    ).slice(0, 5);
                    handleInputChange("working_hours_start", v);
                    if (
                      v.length === 5 &&
                      prev.length === 5 &&
                      v.split(":")[1] !== prev.split(":")[1]
                    ) {
                      setTimeout(() => e.target.blur(), 0);
                    }
                  }}
                  required
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="working_hours_end">Working Hours End *</Label>
                <Input
                  id="working_hours_end"
                  type="time"
                  value={(employeeData.working_hours_end || "").slice(0, 5)}
                  onChange={(e) => {
                    const v = e.target.value;
                    const prev = (
                      employeeData.working_hours_end || ""
                    ).slice(0, 5);
                    handleInputChange("working_hours_end", v);
                    if (
                      v.length === 5 &&
                      prev.length === 5 &&
                      v.split(":")[1] !== prev.split(":")[1]
                    ) {
                      setTimeout(() => e.target.blur(), 0);
                    }
                  }}
                  required
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wifi_ssid">Wifi Name</Label>
                <Input
                  id="wifi_ssid"
                  value={employeeData.wifi_ssid}
                  onChange={(e) => {
                    handleInputChange("wifi_ssid", e.target.value);
                  }}
                  placeholder="Enter wifi name"
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                {/* The label row must be EXACTLY what every other cell has — a
                    bare <Label> and nothing else. Anything added beside it
                    changes this cell's first row height, and in a grid that
                    shifts the input out of line with Longitude's. Two attempts
                    at putting the button up here (a sized <Button>, then a
                    compact inline one) both did it, in opposite directions. The
                    button goes BELOW the input instead, where extra height is
                    harmless — the Radius cell already carries helper text the
                    same way. */}
                <Label htmlFor="lattitude">Latitude</Label>
                <Input
                  id="lattitude"
                  value={employeeData.lattitude}
                  onChange={(e) => {
                    handleInputChange("lattitude", e.target.value);
                  }}
                  placeholder="Enter latitude"
                  disabled={isReadOnly}
                />
                <button
                  type="button"
                  onClick={useCurrentLocation}
                  disabled={isReadOnly || locating}
                  className="text-xs font-medium text-blue-600 hover:underline disabled:opacity-50 disabled:no-underline bg-transparent border-0 p-0"
                >
                  {locating ? "Locating…" : "Use current location"}
                </button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  value={employeeData.longitude}
                  onChange={(e) => {
                    handleInputChange("longitude", e.target.value);
                  }}
                  placeholder="Enter longitude"
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="radius_meters">Radius Meters</Label>
                <Input
                  id="radius_meters"
                  value={employeeData.radius_meters}
                  onChange={(e) => {
                    handleInputChange("radius_meters", e.target.value);
                  }}
                  placeholder="Enter radius in meters"
                  disabled={isReadOnly}
                />
                {/* An office with coordinates but no radius is invisible to the
                    geofence — the save is refused rather than silently creating
                    one, so say what the field is FOR before that happens. */}
                <p className="text-xs text-muted-foreground">
                  Required when coordinates are set. How far from the pin a punch
                  still counts — 50–100m for one floor, 300–500m for a campus.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="allowed_ips">Office IP addresses</Label>
                <Input
                  id="allowed_ips"
                  value={(employeeData as any).allowed_ips || ""}
                  onChange={(e) => {
                    handleInputChange("allowed_ips", e.target.value);
                  }}
                  placeholder="e.g. 203.0.113.7, 203.0.113.0/24"
                  disabled={isReadOnly}
                />
                {!isReadOnly && !detectingIp && detectedIp && (
                  <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2">
                    <span className="text-xs text-muted-foreground">
                      This computer is connecting from
                    </span>
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium">
                      {detectedIp}
                    </code>
                    {detectedIpAlreadyAdded ? (
                      <span className="text-xs text-muted-foreground">
                        — already on the list.
                      </span>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addDetectedIp}
                        >
                          Add it
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          — add it only if you are in the office right now.
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

            </div>

            <div className="flex gap-2 pt-4 justify-end">
              {!isReadOnly && (
              <Button type="submit" disabled={loading} style={{backgroundColor:'lightseagreen'}}>
                {loading ? (
                  "Creating..."
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Save Information
                  </>
                )}
              </Button>
              )}
             
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddOffice;
